/**
 * One knowledge item, edited and moved through its gates. Check runs the automated checks on this
 * version; Promote serves it as a draft; auto-send unlocks only after N drafts sent unchanged in a row
 * (the server refuses it earlier). Saving an edit makes a new version and runs Check. On a promoted
 * item the edit waits: customers keep the served version until the edit passes Check, then it goes
 * live and drafts again until it earns auto-send. Try it shows which answer a message would get and
 * whether it would auto-send.
 */

import { useState } from 'react';
import { AlertTriangle, Check, CircleSlash, Loader2, Lock, Rocket, Search, Trash2, X } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
	useCheckKnowledge,
	useDiscardKnowledgeEdit,
	useEditKnowledge,
	useKnowledgeArticles,
	useKnowledgeAutoSend,
	usePromoteKnowledge,
	useRemoveKnowledge,
	useSelectedBuId,
	useStopKnowledge,
	useTryKnowledge
} from '@/hooks/use-knowledge-promotion';
import { getApiErrorMessage } from '@/lib/utils';
import { ApprovalThreshold } from '@/modules/promotion/approval-threshold';
import { checkOutcomeOf, editableOf, eventLine, linesOf, SOURCE_LABELS, stageBadge, stageOf, textOf } from './status';
import type { KnowledgeCheckResult, KnowledgeEdit, KnowledgeItem, KnowledgePendingEdit, KnowledgeTryResult } from './types';

const NO_ARTICLE = '__none__';

const Section = ({ title, children, testId }: { title: string; children: React.ReactNode; testId?: string }) => (
	<section className='flex flex-col gap-2 border-b px-5 py-4' data-testid={testId}>
		<h3 className='text-xs font-semibold text-foreground'>{title}</h3>
		{children}
	</section>
);

interface CheckListProps {
	check: KnowledgeCheckResult | null;
	articleChanged: boolean;
	/** "this item" or "this edit": what Check has not run on yet. */
	subject: string;
}

const CheckList = ({ check, articleChanged, subject }: CheckListProps) => {
	if (!check) return <p className='text-xs text-muted-foreground'>Check has not run on {subject} yet.</p>;
	return (
		<div className='flex flex-col gap-2'>
			{!check.current && (
				<p className='flex items-start gap-1 text-[11px] text-amber-700'>
					<AlertTriangle className='mt-0.5 size-3 shrink-0' aria-hidden />
					{articleChanged
						? 'Its article changed since Check ran. Edit the answer and run Check, or regenerate from the source if it changed a lot.'
						: 'The item changed since Check ran. Run Check again.'}
				</p>
			)}
			<ul className='flex flex-col gap-2' data-testid='knowledge-check'>
				{check.items.map((entry) => (
					<li key={entry.key} className='flex items-start gap-2 text-[13px]'>
						{entry.ok ? (
							<Check className='mt-0.5 size-4 shrink-0 text-green-700' aria-label='passes' />
						) : entry.blocking ? (
							<X className='mt-0.5 size-4 shrink-0 text-red-700' aria-label='fails' />
						) : (
							<AlertTriangle className='mt-0.5 size-4 shrink-0 text-amber-600' aria-label='warning' />
						)}
						<span>
							{entry.label}
							{entry.detail && <span className='block text-xs text-muted-foreground'>{entry.detail}</span>}
						</span>
					</li>
				))}
			</ul>
		</div>
	);
};

interface FormState {
	question: string;
	answer: string;
	intent: string;
	useWhen: string;
	paraphrases: string;
	tests: string;
	notWhen: string;
	articleId: string;
}

const formOf = (item: KnowledgeItem): FormState => {
	const content = editableOf(item);
	return {
		question: content.question,
		answer: content.answer,
		intent: content.intent,
		useWhen: content.useWhen,
		paraphrases: textOf(content.paraphrases),
		tests: textOf(content.tests),
		notWhen: textOf(content.notWhen),
		articleId: content.article?.id ?? NO_ARTICLE
	};
};

/** Only the fields that changed, so a save never rewrites what the user did not touch. */
const changesOf = (item: KnowledgeItem, form: FormState): KnowledgeEdit => {
	const before = formOf(item);
	const edit: KnowledgeEdit = {};
	if (form.question !== before.question) edit.question = form.question;
	if (form.answer !== before.answer) edit.answer = form.answer;
	if (form.intent !== before.intent) edit.intent = form.intent;
	if (form.useWhen !== before.useWhen) edit.useWhen = form.useWhen;
	if (form.paraphrases !== before.paraphrases) edit.paraphrases = linesOf(form.paraphrases);
	if (form.tests !== before.tests) edit.tests = linesOf(form.tests);
	if (form.notWhen !== before.notWhen) edit.notWhen = linesOf(form.notWhen);
	if (form.articleId !== before.articleId) edit.articleId = form.articleId === NO_ARTICLE ? null : form.articleId;
	return edit;
};

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
	<div className='flex flex-col gap-1'>
		<Label className='text-[11px] font-medium'>{label}</Label>
		{children}
		{hint && <span className='text-[11px] text-muted-foreground'>{hint}</span>}
	</div>
);

const EditForm = ({ item, canEdit }: { item: KnowledgeItem; canEdit: boolean }) => {
	const [form, setForm] = useState<FormState>(() => formOf(item));
	const edit = useEditKnowledge();
	const check = useCheckKnowledge();
	const { data: articles } = useKnowledgeArticles();
	const changes = changesOf(item, form);
	const dirty = Object.keys(changes).length > 0;
	const set = (field: keyof FormState) => (value: string) => setForm((current) => ({ ...current, [field]: value }));
	const busy = edit.isPending || check.isPending;

	const save = async () => {
		try {
			await edit.mutateAsync({ id: item.id, edit: changes });
			toast.success(
				item.state === 'promoted'
					? `Saved. Customers keep version ${item.version} until the edit passes Check; running Check…`
					: 'Saved; running Check…'
			);
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not save'));
			return;
		}
		try {
			const outcome = checkOutcomeOf(item, await check.mutateAsync({ id: item.id }));
			toast[outcome.ok ? 'success' : 'warning'](outcome.message);
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Check could not run'));
		}
	};

	return (
		<div className='flex flex-col gap-3' data-testid='knowledge-edit'>
			<Field label='Question'>
				<Input value={form.question} onChange={(event) => set('question')(event.target.value)} disabled={!canEdit} />
			</Field>
			<Field label='Answer' hint='Sent as written; the agent only fills in customer details.'>
				<Textarea rows={6} value={form.answer} onChange={(event) => set('answer')(event.target.value)} disabled={!canEdit} />
			</Field>
			<Field label='Use when' hint='The situation this answer assumes.'>
				<Input value={form.useWhen} onChange={(event) => set('useWhen')(event.target.value)} disabled={!canEdit} />
			</Field>
			<Field
				label='Not when'
				hint='One per line: messages that sound close but must not get this answer. Check makes sure none of them would auto-send it.'
			>
				<Textarea rows={4} value={form.notWhen} onChange={(event) => set('notWhen')(event.target.value)} disabled={!canEdit} />
			</Field>
			<Field
				label='Other ways to ask'
				hint='One per line. Each is searchable, so customers who word it differently still find this answer.'
			>
				<Textarea rows={4} value={form.paraphrases} onChange={(event) => set('paraphrases')(event.target.value)} disabled={!canEdit} />
			</Field>
			<Field label='Test questions' hint='One per line. Never searchable: Check asks them to see whether they find this answer.'>
				<Textarea rows={4} value={form.tests} onChange={(event) => set('tests')(event.target.value)} disabled={!canEdit} />
			</Field>
			<div className='grid grid-cols-2 gap-3'>
				<Field label='Intent'>
					<Input value={form.intent} onChange={(event) => set('intent')(event.target.value)} disabled={!canEdit} />
				</Field>
				<Field label='Article'>
					<Select value={form.articleId} onValueChange={set('articleId')} disabled={!canEdit}>
						<SelectTrigger className='h-9 text-xs'>
							<SelectValue placeholder='No article' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={NO_ARTICLE}>No article</SelectItem>
							{item.article && !articles?.some((article) => article.id === item.article!.id) && (
								<SelectItem value={item.article.id}>{item.article.name ?? item.article.id}</SelectItem>
							)}
							{articles?.map((article) => (
								<SelectItem key={article.id} value={article.id}>
									{article.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</Field>
			</div>
			{item.quote && (
				<Field label='Backed by this passage'>
					<blockquote className='rounded-md border-l-2 bg-muted/40 px-3 py-2 text-[12px] italic text-muted-foreground'>
						{item.quote}
					</blockquote>
				</Field>
			)}
			{canEdit && (
				<div className='flex items-center justify-between gap-2'>
					<span className='text-[11px] text-muted-foreground'>
						{item.state === 'promoted'
							? 'Customers keep the live version until your edit passes Check. Then it goes live as a draft, and its run of approvals starts again.'
							: `Version ${item.version}`}
					</span>
					<div className='flex gap-1.5'>
						<Button size='sm' variant='ghost' disabled={!dirty || busy} onClick={() => setForm(formOf(item))}>
							Discard
						</Button>
						<Button size='sm' disabled={!dirty || busy || !form.question.trim() || !form.answer.trim()} onClick={save}>
							{busy && <Loader2 className='size-3.5 animate-spin' aria-hidden />}
							Save and check
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

interface WaitingEditProps {
	item: KnowledgeItem;
	pending: KnowledgePendingEdit;
	canEdit: boolean;
}

/** A promoted item's edit that has not passed Check: what customers get meanwhile, and a way out. */
const WaitingEdit = ({ item, pending, canEdit }: WaitingEditProps) => {
	const discard = useDiscardKnowledgeEdit();

	const run = async () => {
		try {
			await discard.mutateAsync({ id: item.id });
			toast.success(`Edit discarded: customers keep version ${item.version}`);
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not discard the edit'));
		}
	};

	return (
		<div className='flex flex-col gap-1.5 rounded-md border bg-muted/40 p-2' data-testid='knowledge-waiting-edit'>
			<p className='flex items-start gap-1 text-xs font-medium text-amber-700'>
				<AlertTriangle className='mt-0.5 size-3 shrink-0' aria-hidden />
				Customers get version {item.version}. Your edit (version {pending.version}) goes live once it passes Check.
			</p>
			{pending.check && !pending.check.ok && (
				<p className='text-[11px] text-muted-foreground'>Check failed on the edit: fix it below and save, or discard it.</p>
			)}
			<div className='text-[11px] text-muted-foreground'>
				<span className='font-medium text-foreground'>Live answer: </span>
				{item.answer}
			</div>
			{canEdit && (
				<Button size='sm' variant='ghost' className='self-start' disabled={discard.isPending} onClick={run}>
					{discard.isPending && <Loader2 className='size-3.5 animate-spin' aria-hidden />}
					Discard edit
				</Button>
			)}
		</div>
	);
};

const TryIt = ({ item }: { item: KnowledgeItem }) => {
	const [question, setQuestion] = useState('');
	const [result, setResult] = useState<KnowledgeTryResult | null>(null);
	const tryIt = useTryKnowledge();
	const buId = useSelectedBuId();

	const run = async () => {
		try {
			setResult(await tryIt.mutateAsync({ question, itemId: item.id, buId }));
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not try it'));
		}
	};

	return (
		<div className='flex flex-col gap-2' data-testid='knowledge-try'>
			<div className='flex gap-1.5'>
				<Input
					placeholder='Type a customer message'
					value={question}
					onChange={(event) => setQuestion(event.target.value)}
					onKeyDown={(event) => event.key === 'Enter' && question.trim() && run()}
				/>
				<Button size='sm' variant='outline' disabled={!question.trim() || tryIt.isPending} onClick={run}>
					{tryIt.isPending ? <Loader2 className='size-3.5 animate-spin' aria-hidden /> : <Search className='size-3.5' aria-hidden />}
					Try
				</Button>
			</div>
			{result && (
				<div className='flex flex-col gap-1.5 rounded-md border p-2' data-testid='knowledge-try-result'>
					<Badge
						variant={result.outcome === 'auto_send' ? 'success' : result.outcome === 'draft' ? 'secondary' : 'outline'}
						className='self-start text-[10px]'
					>
						{result.outcome === 'auto_send' ? 'Would auto-send' : result.outcome === 'draft' ? 'Would draft' : 'No promoted answer'}
					</Badge>
					<p className='text-[12px]'>{result.reason}</p>
					{result.matches.map((match) => (
						<p key={match.itemId} className='flex items-center justify-between gap-2 text-[11px] text-muted-foreground'>
							<span className={match.itemId === item.id ? 'font-medium text-foreground' : ''}>{match.question}</span>
							<span className='shrink-0 tabular-nums'>{match.score.toFixed(2)}</span>
						</p>
					))}
					{result.itemScore !== null && item.state !== 'promoted' && (
						<p className='text-[11px] text-muted-foreground'>
							This item is not served yet; the message is {result.itemScore.toFixed(2)} close to it.
						</p>
					)}
				</div>
			)}
		</div>
	);
};

interface KnowledgeItemPanelProps {
	item: KnowledgeItem;
	canEdit: boolean;
	onClose: () => void;
}

export const KnowledgeItemPanel = ({ item, canEdit, onClose }: KnowledgeItemPanelProps) => {
	const check = useCheckKnowledge();
	const promote = usePromoteKnowledge();
	const stop = useStopKnowledge();
	const remove = useRemoveKnowledge();
	const autoSend = useKnowledgeAutoSend();
	const badge = stageBadge(item);
	const busy = check.isPending || promote.isPending || stop.isPending || remove.isPending || autoSend.isPending;

	const act = async (run: () => Promise<unknown>, success: string, failure: string) => {
		try {
			await run();
			toast.success(success);
		} catch (error) {
			toast.error(getApiErrorMessage(error, failure));
		}
	};

	// With an edit waiting, Check decides whether it goes live: say which happened.
	const runCheck = async () => {
		if (!item.pending) return act(() => check.mutateAsync({ id: item.id }), 'Check finished', 'Check could not run');
		try {
			const outcome = checkOutcomeOf(item, await check.mutateAsync({ id: item.id }));
			toast[outcome.ok ? 'success' : 'warning'](outcome.message);
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Check could not run'));
		}
	};

	return (
		<aside className='flex h-full w-[520px] shrink-0 flex-col border-l bg-background' data-testid='knowledge-panel'>
			<div className='flex items-start justify-between gap-2 border-b px-5 py-4'>
				<div className='min-w-0'>
					<p className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>Knowledge item</p>
					<h2 className='text-sm font-semibold text-foreground'>{item.question}</h2>
					<div className='mt-1 flex flex-wrap items-center gap-1.5'>
						<Badge variant={badge.variant} className='text-[10px]'>
							{badge.label}
						</Badge>
						<span className='text-[11px] text-muted-foreground'>
							{SOURCE_LABELS[item.source]}
							{item.article ? ` · ${item.article.name ?? 'article'}` : ''} · v{item.version}
						</span>
					</div>
				</div>
				<Button variant='ghost' size='icon' className='size-7 shrink-0' onClick={onClose} aria-label='Close'>
					<X className='size-4' />
				</Button>
			</div>

			<div className='flex-1 overflow-y-auto'>
				<Section title='Where it stands' testId='knowledge-status'>
					{item.article?.missing && (
						<p className='flex items-start gap-1 text-[11px] text-red-700'>
							<AlertTriangle className='mt-0.5 size-3 shrink-0' aria-hidden />
							Its article was deleted.
						</p>
					)}
					{item.state === 'candidate' ? (
						<p className='text-xs text-muted-foreground'>Not served. Promote it to serve it as a draft an agent reviews.</p>
					) : (
						<p className='text-xs text-muted-foreground'>
							{item.autoSend ? 'Auto-sends when a message matches it clearly.' : 'Serves as a draft an agent reviews.'}
							{!item.enabled && ' It is switched off.'}
						</p>
					)}
					{item.pending && <WaitingEdit item={item} pending={item.pending} canEdit={canEdit} />}
					{stageOf(item) === 'serving_check_failed' && (
						<p className='flex items-start gap-1 text-[11px] text-red-700' data-testid='knowledge-serving-check-failed'>
							<AlertTriangle className='mt-0.5 size-3 shrink-0' aria-hidden />
							Its last Check failed, and it still serves. Fix the answer and run Check again.
						</p>
					)}
					{canEdit && (
						<div className='flex flex-wrap gap-1.5'>
							<Button size='sm' variant='outline' disabled={busy} onClick={runCheck}>
								{check.isPending && <Loader2 className='size-3.5 animate-spin' aria-hidden />}
								Run Check
							</Button>
							{item.state === 'candidate' ? (
								<>
									<Button
										size='sm'
										disabled={busy || item.promoteLock !== null}
										onClick={() => act(() => promote.mutateAsync({ id: item.id }), 'Promoted: it serves as a draft', 'Could not promote')}
									>
										<Rocket className='size-3.5' aria-hidden />
										Promote
									</Button>
									<Button
										size='sm'
										variant='ghost'
										disabled={busy}
										onClick={() => act(() => remove.mutateAsync({ id: item.id }).then(onClose), 'Deleted', 'Could not delete')}
									>
										<Trash2 className='size-3.5' aria-hidden />
										Delete
									</Button>
								</>
							) : (
								<Button
									size='sm'
									variant='outline'
									disabled={busy}
									onClick={() => act(() => stop.mutateAsync({ id: item.id }), 'Stopped serving it', 'Could not stop it')}
								>
									<CircleSlash className='size-3.5' aria-hidden />
									Stop serving
								</Button>
							)}
						</div>
					)}
					{item.state === 'candidate' && item.promoteLock && (
						<p className='flex items-center gap-1 text-[11px] text-muted-foreground' data-testid='promote-lock'>
							<Lock className='size-3' aria-hidden />
							{item.promoteLock}
						</p>
					)}
					{item.state === 'promoted' && (
						<div className='flex flex-col gap-1.5 rounded-md bg-muted/40 p-2' data-testid='knowledge-auto-send'>
							<div className='flex items-center justify-between gap-2'>
								<span className='text-xs font-medium'>Auto-send</span>
								<span className='flex items-center gap-2'>
									<Badge variant={item.streak.count >= item.streak.required ? 'success' : 'secondary'} className='text-[10px]'>
										{Math.min(item.streak.count, item.streak.required)}/{item.streak.required} sent unchanged
									</Badge>
									<Switch
										aria-label='Auto-send'
										checked={item.autoSend}
										disabled={!canEdit || busy || (!item.autoSend && item.autoSendLock !== null)}
										onCheckedChange={(checked) =>
											act(
												() => autoSend.mutateAsync({ id: item.id, autoSend: checked }),
												checked ? 'It auto-sends now' : 'It drafts again',
												'Could not change auto-send'
											)
										}
									/>
								</span>
							</div>
							{!item.autoSend && item.autoSendLock && (
								<p className='flex items-center gap-1 text-[11px] text-muted-foreground'>
									<Lock className='size-3' aria-hidden />
									{item.autoSendLock}
								</p>
							)}
							<div className='text-[11px] text-muted-foreground'>
								<ApprovalThreshold kind='knowledge' />
							</div>
						</div>
					)}
				</Section>

				{item.pending ? (
					<Section title={`Check on your edit (version ${item.pending.version})`}>
						<CheckList check={item.pending.check} articleChanged={item.pending.article?.changed ?? false} subject='this edit' />
					</Section>
				) : (
					<Section title='Check'>
						<CheckList check={item.check} articleChanged={item.article?.changed ?? false} subject='this item' />
					</Section>
				)}

				<Section title={item.pending ? 'Your edit: question, answer and scope' : 'Question, answer and scope'}>
					<EditForm key={`${item.id}:${item.pending?.version ?? item.version}`} item={item} canEdit={canEdit} />
				</Section>

				<Section title='Try it'>
					<TryIt item={item} />
				</Section>

				<Section title='History'>
					<ul className='flex flex-col gap-1' data-testid='knowledge-history'>
						{item.events.slice(0, 15).map((event, index) => (
							<li key={`${event.at}-${index}`} className='flex justify-between gap-2 text-[11px]'>
								<span>{eventLine(event)}</span>
								<span className='shrink-0 text-muted-foreground'>{new Date(event.at).toLocaleString()}</span>
							</li>
						))}
						{item.events.length === 0 && <li className='text-[11px] text-muted-foreground'>Nothing yet.</li>}
					</ul>
				</Section>
			</div>
		</aside>
	);
};
